/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import useGraphClient from '../../utils/microsoft/useGraphClient';
import { type OneDriveFile, type OneDriveFolder } from './types';
import OneDriveItemTableRow from './OneDriveItemTableRow';
import FilesTableHeader from './FilesTableHeader';
import FolderBreadcrumbs from './FolderBreadcrumbs';
import EmptyFolderMessage from './messages/EmptyFolderMessage';
import EmptyOneDriveMessage from './messages/EmptyOneDriveMessage';
import OneDriveLoadingMessage from './messages/OneDriveLoadingMessage';
import useOneDriveFilesState from './useOneDriveFilesState';
import FilesTable from './styled-components/FilesTable';
import FilesTableContent from './styled-components/FilesTableContent';
import OneDriveErrorMessage from './messages/OneDriveErrorMessage';
import { GraphError } from '@microsoft/microsoft-graph-client';
import FilesTableWrapper from './styled-components/FilesTableWrapper';
import PickerDialogTitle from './styled-components/PickerDialogTitle';
import OneDriveIcon from '../../utils/icons/OneDriveIcon';
import Dialog from '@/components/Dialog';
import { IconButton, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import oneDriveFilesCache from './oneDriveFilesCache';
import * as Sentry from '@sentry/react';
import { FormattedMessage } from 'react-intl';

export type OneDriveFilesResponse = {
  value: (OneDriveFile | OneDriveFolder)[];
};

const FilePicker = () => {
  const {
    activeFolder,
    openFolder,
    closeFolder,
    folderBreadcrumbs,
    setRootFiles,
    resetFilesState,
  } = useOneDriveFilesState();

  const graphClient = useGraphClient();

  const [showError, setShowError] = useState(false);

  const loadRootFiles = async (): Promise<void> => {
    try {
      const rootFiles: OneDriveFilesResponse = await graphClient
        .api('/me/drive/root/children')
        .get();

      setRootFiles(rootFiles.value);
    } catch (error) {
      Sentry.captureException(error);

      if (
        error instanceof GraphError &&
        error.message.includes('interaction_in_progress')
      ) {
        // no-op
        return;
      }

      setShowError(true);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadRootFiles();
  }, []);

  const refreshAllFiles = (): void => {
    resetFilesState();
    oneDriveFilesCache.reset();
    setShowError(false);

    loadRootFiles();
  };

  const loading = activeFolder === null;

  return (
    <>
      <PickerDialogTitle>
        <OneDriveIcon />
        <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.dialog_title" />
        <Tooltip
          title={
            <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.refresh_button" />
          }
        >
          <IconButton size="small" onClick={refreshAllFiles}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </PickerDialogTitle>
      <Dialog.Content>
        <Dialog.ContentText>
          <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.dialog_text" />
        </Dialog.ContentText>
      </Dialog.Content>
      <FolderBreadcrumbs
        breadcrumbs={folderBreadcrumbs}
        onCloseFolder={closeFolder}
      />
      <FilesTableWrapper>
        <FilesTable>
          <FilesTableHeader />
          {!loading && activeFolder.items.length > 0 && (
            <FilesTableContent>
              {activeFolder.items.map(
                (fileOrFolder: OneDriveFile | OneDriveFolder) => (
                  <OneDriveItemTableRow
                    oneDriveItem={fileOrFolder}
                    onOpenFolder={openFolder}
                    key={fileOrFolder.id}
                  />
                ),
              )}
            </FilesTableContent>
          )}
        </FilesTable>
        {showError && <OneDriveErrorMessage />}
        {loading && !showError && <OneDriveLoadingMessage />}
        {!loading &&
          !showError &&
          activeFolder.items.length === 0 &&
          !activeFolder.isRootFolder && <EmptyFolderMessage />}
        {!loading &&
          !showError &&
          activeFolder.items.length === 0 &&
          activeFolder.isRootFolder && <EmptyOneDriveMessage />}
      </FilesTableWrapper>
    </>
  );
};

export default FilePicker;
